// ============================================================
// TMR Portal Admin — reading a conta corrente (14)
// ============================================================
import type { CurrentAccount } from './contract';

/**
 * The account's own vocabulary, in the operator's words.
 *
 * Only `Active` is ever written today; the other two are declared because the backend's enum is persisted
 * and a future value must not surface as a blank cell.
 */
const STATUS_LABEL: Record<string, string> = {
  Active: 'Ativa',
  Suspended: 'Suspensa',
  Closed: 'Encerrada',
};

/**
 * The account's situation as it is read on screen.
 *
 * An unregistered key shows itself rather than falling back to a real status — the same lesson the rate
 * badge learned the hard way: a plausible fallback does not look broken, it looks *wrong*, which is much
 * harder to notice.
 *
 * @param status The status as the backend sent it.
 */
export function accountStatusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status;
}

/**
 * Whether the account exists here but has not reached the legacy yet.
 *
 * Worth showing, because it has a consequence the operator can act on: until the row exists over there,
 * the legacy's repasse flow cannot see this account at all. It resolves itself once the Empresa is
 * exported (14 §7), so the screen explains rather than offering a button.
 */
export function isAwaitingLegacyReplication(account: CurrentAccount): boolean {
  return account.legacyAccountId === null;
}

/** How the withdrawal lock reads on screen. */
export interface WithdrawalSummary {
  /** Which {@link Notice} tone carries it. */
  tone: 'success' | 'warning';
  /** The headline — what the operator needs to know at a glance. */
  title: string;
  /** Why it is that way, and what changes it. */
  detail: string;
  /** Whether to offer the link to Contratos, which is where the state actually changes. */
  linkToContracts: boolean;
}

/**
 * Describes the withdrawal lock (14 §3).
 *
 * The lock is a consequence of the contract, never an operator decision, so this returns wording and a
 * pointer — never an action. A switch here would be a second source of truth for the same fact.
 *
 * @param account The Empresa's conta corrente.
 */
export function withdrawalSummary(account: CurrentAccount): WithdrawalSummary {
  if (account.withdrawalAllowed) {
    return {
      tone: 'success',
      title: 'Saque liberado.',
      detail: 'O contrato desta empresa está assinado.',
      linkToContracts: false,
    };
  }

  return {
    tone: 'warning',
    title: 'Saque bloqueado.',
    detail: 'A trava é aberta automaticamente quando o contrato é assinado por completo.',
    linkToContracts: true,
  };
}
